// react
import { ReactNode } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';

// icons
import {
	CheckCircleRounded as IconCheckFilled,
	CheckCircleOutlineRounded as IconCheckOutline,
	TuneRounded as IconTune,
} from '@mui/icons-material';

// types

export type CommitmentType = 'full' | 'partial';

export interface CommitmentTypeSelectorProps {
	value: CommitmentType;
	onChange: (value: CommitmentType) => void;
	/** Force the user into "partial" (e.g. contract amount exceeds activity budget). */
	mustUsePartial?: boolean;
	/** Optional override copy. */
	fullLabel?: string;
	fullDescription?: string;
	partialLabel?: string;
	partialDescription?: string;
	/** Optional reason text rendered under the disabled "Full" card. */
	disabledReason?: ReactNode;
}

// Component

/**
 * Modern two-up card selector for commitment type (full vs. partial).
 *
 * Used by both the V2 "Link Contract" form and the V2 "Create Contract"
 * form's spend-plan section so the experience is identical across the
 * two flows.
 */
export const CommitmentTypeSelector = ({
	value,
	onChange,
	mustUsePartial = false,
	fullLabel,
	fullDescription,
	partialLabel,
	partialDescription,
	disabledReason,
}: CommitmentTypeSelectorProps) => {
	const { t } = useTranslation();
	const theme = useTheme();

	const resolvedFullLabel = fullLabel ?? t('commitmentType.fullAmount');
	const resolvedFullDescription = fullDescription ?? t('commitmentType.fullDescription');
	const resolvedPartialLabel = partialLabel ?? t('commitmentType.partialAmount');
	const resolvedPartialDescription = partialDescription ?? t('commitmentType.partialDescription');

	const cardSx = (selected: boolean, disabled: boolean) => ({
		flex: 1,
		minWidth: 0,
		position: 'relative' as const,
		display: 'flex',
		alignItems: 'flex-start',
		gap: 1.5,
		px: 2,
		py: 1.75,
		borderRadius: 2,
		border: '1.5px solid',
		borderColor: selected ? 'primary.main' : alpha(theme.palette.divider, 0.7),
		backgroundColor: selected
			? alpha(theme.palette.primary.main, 0.05)
			: theme.palette.background.paper,
		cursor: disabled ? 'not-allowed' : 'pointer',
		opacity: disabled ? 0.55 : 1,
		transition: theme.transitions.create(
			['border-color', 'background-color', 'box-shadow', 'transform'],
			{ duration: theme.transitions.duration.shorter }
		),
		boxShadow: selected ? `0 1px 2px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
		'&:hover': disabled
			? undefined
			: {
					borderColor: selected ? 'primary.dark' : alpha(theme.palette.primary.main, 0.4),
					backgroundColor: selected
						? alpha(theme.palette.primary.main, 0.07)
						: alpha(theme.palette.primary.main, 0.02),
				},
		'&:focus-visible': {
			outline: 'none',
			boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
		},
	});

	const iconBadgeSx = (selected: boolean) => ({
		flex: '0 0 auto',
		width: 32,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 1.25,
		color: selected ? 'primary.main' : 'text.secondary',
		backgroundColor: selected
			? alpha(theme.palette.primary.main, 0.12)
			: alpha(theme.palette.text.primary, 0.04),
		transition: theme.transitions.create(['color', 'background-color'], {
			duration: theme.transitions.duration.shorter,
		}),
	});

	const handleSelect = (next: CommitmentType, disabled: boolean) => {
		if (disabled) return;
		if (value !== next) onChange(next);
	};

	const fullDisabled = mustUsePartial;
	const fullSelected = value === 'full' && !fullDisabled;
	const partialSelected = value === 'partial' || fullDisabled;

	return (
		<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
			<Box
				role="radio"
				aria-checked={fullSelected}
				aria-disabled={fullDisabled}
				tabIndex={fullDisabled ? -1 : 0}
				onClick={() => handleSelect('full', fullDisabled)}
				onKeyDown={(e) => {
					if ((e.key === ' ' || e.key === 'Enter') && !fullDisabled) {
						e.preventDefault();
						handleSelect('full', fullDisabled);
					}
				}}
				sx={cardSx(fullSelected, fullDisabled)}>
				<Box sx={iconBadgeSx(fullSelected)}>
					{fullSelected ? (
						<IconCheckFilled fontSize="small" />
					) : (
						<IconCheckOutline fontSize="small" />
					)}
				</Box>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography
						variant="subtitle2"
						sx={{
							fontWeight: 600,
							color: fullSelected ? 'primary.main' : 'text.primary',
							lineHeight: 1.3,
						}}>
					{resolvedFullLabel}
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
					{resolvedFullDescription}
					</Typography>
					{fullDisabled && disabledReason && (
						<Typography
							variant="caption"
							color="warning.dark"
							sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
							{disabledReason}
						</Typography>
					)}
				</Box>
			</Box>

			<Box
				role="radio"
				aria-checked={partialSelected}
				tabIndex={0}
				onClick={() => handleSelect('partial', false)}
				onKeyDown={(e) => {
					if (e.key === ' ' || e.key === 'Enter') {
						e.preventDefault();
						handleSelect('partial', false);
					}
				}}
				sx={cardSx(partialSelected, false)}>
				<Box sx={iconBadgeSx(partialSelected)}>
					<IconTune fontSize="small" />
				</Box>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography
						variant="subtitle2"
						sx={{
							fontWeight: 600,
							color: partialSelected ? 'primary.main' : 'text.primary',
							lineHeight: 1.3,
						}}>
					{resolvedPartialLabel}
				</Typography>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
					{resolvedPartialDescription}
					</Typography>
				</Box>
			</Box>
		</Stack>
	);
};
